import React from 'react'
import joinClassNames from './joinClassNames.js'

/**
 * Removable active-filter chip (Cook recipe filters). Maps to `.recipe-filter-chip`.
 *
 * @param {{
 *   label: React.ReactNode,
 *   startIcon?: React.ReactNode,
 *   onRemove?: () => void,
 *   removeLabel?: string,
 *   className?: string,
 * } & React.HTMLAttributes<HTMLSpanElement>} props
 */
export function FilterChip({
  label,
  startIcon,
  onRemove,
  removeLabel,
  className = '',
  ...rest
}) {
  const resolvedRemoveLabel =
    removeLabel || (typeof label === 'string' ? `Remove ${label} filter` : 'Remove filter')

  return (
    <span className={joinClassNames('recipe-filter-chip', className)} {...rest}>
      {startIcon}
      {label}
      {typeof onRemove === 'function' ? (
        <button
          type="button"
          className="recipe-filter-chip-x"
          onClick={onRemove}
          aria-label={resolvedRemoveLabel}
        >
          ×
        </button>
      ) : null}
    </span>
  )
}

/**
 * Toggle filter chip (select/deselect). Maps to `.filter-chip-toggle`.
 *
 * @param {{
 *   label: React.ReactNode,
 *   active?: boolean,
 *   adornment?: React.ReactNode,
 *   onClick?: () => void,
 *   onClear?: () => void,
 *   clearLabel?: string,
 *   className?: string,
 * } & React.ButtonHTMLAttributes<HTMLButtonElement>} props
 */
export function FilterChipToggle({
  label,
  active = false,
  adornment,
  onClick,
  onClear,
  clearLabel,
  className = '',
  type = 'button',
  ...rest
}) {
  const resolvedClearLabel =
    clearLabel || (typeof label === 'string' ? `Clear ${label} filter` : 'Clear filter')

  return (
    <button
      type={type}
      className={joinClassNames(
        'filter-chip-toggle',
        active ? 'filter-chip-toggle--active' : '',
        className,
      )}
      onClick={onClick}
      {...rest}
    >
      {adornment ? <span className="filter-chip-toggle__adornment">{adornment}</span> : null}
      {label}
      {active && typeof onClear === 'function' ? (
        <span
          className="filter-chip-toggle__clear"
          role="button"
          tabIndex={0}
          onClick={(event) => {
            event.stopPropagation()
            onClear()
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              event.stopPropagation()
              onClear()
            }
          }}
          aria-label={resolvedClearLabel}
        >
          ×
        </span>
      ) : null}
    </button>
  )
}

/**
 * Text control to clear all filters in a row.
 */
export function FilterChipsClear({ children = 'Clear all', className = '', ...rest }) {
  return (
    <button
      type="button"
      className={joinClassNames('recipe-filter-chips-clear', className)}
      {...rest}
    >
      {children}
    </button>
  )
}

export default FilterChip
